// client/src/components/pages/AmendmentsAllocateTransactions/DynamicColumnSelector.tsx
// (converted from .js, ST-00221). The "Add Column Filter" picker of the dynamic filters.
import { useState } from 'react';
import {
  PlusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  TableCellsIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/20/solid';
import ThemedSelect from '../../common/ThemedSelect';

export type DynamicColumnType = 'text' | 'number' | 'date' | 'datetime' | 'boolean';

/** A data-view column a dynamic filter can target (its name is what the server resolves). */
export interface DynamicColumn {
  name: string;
  label: string;
  category: string;
  type: DynamicColumnType;
  description: string;
}

// All available columns from LexiBridgeDataView with categorization and user-friendly labels.
// "Is Active Amendment" is gone: it is not a data-view column, so the server silently ignored
// the filter (AMD-24). Load Date, Is Amended, Import Source/Timestamp and Source File exist in
// the data view but not in the history table: the History tab answers 400 for them.
export const AVAILABLE_COLUMNS: readonly DynamicColumn[] = [
  // Basic Information
  {
    name: 'TicketUID',
    label: 'Ticket UID',
    category: 'basic',
    type: 'text',
    description: 'Unique ticket identifier',
  },
  {
    name: 'Ticket No',
    label: 'Ticket Number',
    category: 'basic',
    type: 'text',
    description: 'Ticket number',
  },
  {
    name: 'Delivery No',
    label: 'Delivery Number',
    category: 'basic',
    type: 'text',
    description: 'Delivery number',
  },
  {
    name: 'Order No',
    label: 'Order Number',
    category: 'basic',
    type: 'text',
    description: 'Order number',
  },
  {
    name: 'Revision No',
    label: 'Revision Number',
    category: 'basic',
    type: 'text',
    description: 'Revision number',
  },
  {
    name: 'Ticket Type',
    label: 'Ticket Type',
    category: 'basic',
    type: 'text',
    description: 'Type of ticket (e.g., Inbound, Outbound)',
  },
  {
    name: 'Ref No',
    label: 'Reference Number',
    category: 'basic',
    type: 'text',
    description: 'Reference number',
  },

  // Location Information
  {
    name: 'Dispatch Organisation',
    label: 'Dispatch Organisation',
    category: 'location',
    type: 'text',
    description: 'Organization dispatching the goods',
  },
  {
    name: 'Dispatch Location',
    label: 'Dispatch Location',
    category: 'location',
    type: 'text',
    description: 'Location where goods are dispatched from',
  },
  {
    name: 'Receipt Organisation',
    label: 'Receipt Organisation',
    category: 'location',
    type: 'text',
    description: 'Organization receiving the goods',
  },
  {
    name: 'Receipt Location',
    label: 'Receipt Location',
    category: 'location',
    type: 'text',
    description: 'Location where goods are received',
  },

  // Weight Information
  {
    name: 'First Weight',
    label: 'First Weight',
    category: 'weight',
    type: 'number',
    description: 'First weight measurement',
  },
  {
    name: 'Second Weight',
    label: 'Second Weight',
    category: 'weight',
    type: 'number',
    description: 'Second weight measurement',
  },
  {
    name: 'Nett Weight',
    label: 'Nett Weight',
    category: 'weight',
    type: 'number',
    description: 'Net weight (Second - First weight)',
  },
  {
    name: 'Delivery Weight',
    label: 'Delivery Weight',
    category: 'weight',
    type: 'number',
    description: 'Weight at delivery',
  },
  {
    name: 'Variance Weight',
    label: 'Variance Weight',
    category: 'weight',
    type: 'number',
    description: 'Weight variance',
  },
  {
    name: 'Dispatch Nett Weight',
    label: 'Dispatch Nett Weight',
    category: 'weight',
    type: 'number',
    description: 'Net weight at dispatch',
  },
  {
    name: 'Variance Tonnes',
    label: 'Variance (Tonnes)',
    category: 'weight',
    type: 'number',
    description: 'Variance in tonnes',
  },
  {
    name: 'Variance %',
    label: 'Variance Percentage',
    category: 'weight',
    type: 'number',
    description: 'Variance as percentage',
  },

  // Transport Information
  {
    name: 'Haulier',
    label: 'Haulier',
    category: 'transport',
    type: 'text',
    description: 'Transport company',
  },
  {
    name: 'Reg No',
    label: 'Registration Number',
    category: 'transport',
    type: 'text',
    description: 'Vehicle registration number',
  },
  {
    name: 'Driver',
    label: 'Driver',
    category: 'transport',
    type: 'text',
    description: 'Driver name',
  },
  {
    name: 'Trailer Reg 1',
    label: 'Trailer Registration 1',
    category: 'transport',
    type: 'text',
    description: 'First trailer registration',
  },
  {
    name: 'Trailer Reg 2',
    label: 'Trailer Registration 2',
    category: 'transport',
    type: 'text',
    description: 'Second trailer registration',
  },

  // Product Information
  {
    name: 'Product',
    label: 'Product',
    category: 'product',
    type: 'text',
    description: 'Product name',
  },
  {
    name: 'Product Ref.',
    label: 'Product Reference',
    category: 'product',
    type: 'text',
    description: 'Product reference code',
  },

  // Security Information
  {
    name: 'Security Seal 1',
    label: 'Security Seal 1',
    category: 'security',
    type: 'text',
    description: 'First security seal number',
  },
  {
    name: 'Security Seal 2',
    label: 'Security Seal 2',
    category: 'security',
    type: 'text',
    description: 'Second security seal number',
  },

  // Timestamp Information
  {
    name: 'Ticket Timestamp',
    label: 'Ticket Timestamp',
    category: 'timestamp',
    type: 'datetime',
    description: 'When the ticket was created',
  },
  {
    name: 'First Weight Timestamp',
    label: 'First Weight Timestamp',
    category: 'timestamp',
    type: 'datetime',
    description: 'When first weight was recorded',
  },
  {
    name: 'Second Weight Timestamp',
    label: 'Second Weight Timestamp',
    category: 'timestamp',
    type: 'datetime',
    description: 'When second weight was recorded',
  },
  {
    name: 'Time Between Weights',
    label: 'Time Between Weights',
    category: 'timestamp',
    type: 'text',
    description: 'Duration between weight measurements',
  },

  // Tag Information
  {
    name: 'Tag1',
    label: 'Tag 1',
    category: 'tags',
    type: 'text',
    description: 'Custom tag field 1',
  },
  {
    name: 'Tag2',
    label: 'Tag 2',
    category: 'tags',
    type: 'text',
    description: 'Custom tag field 2',
  },
  {
    name: 'Tag3',
    label: 'Tag 3',
    category: 'tags',
    type: 'text',
    description: 'Custom tag field 3',
  },
  {
    name: 'Tag4',
    label: 'Tag 4',
    category: 'tags',
    type: 'text',
    description: 'Custom tag field 4',
  },

  // Amendment Information
  {
    name: 'Version',
    label: 'Version',
    category: 'amendment',
    type: 'number',
    description: 'Amendment version number',
  },
  {
    name: 'AmendmentTimestamp',
    label: 'Amendment Timestamp',
    category: 'amendment',
    type: 'datetime',
    description: 'When amendment was made',
  },
  {
    name: 'AmendmentUser',
    label: 'Amendment User',
    category: 'amendment',
    type: 'text',
    description: 'User who made the amendment',
  },
  {
    name: 'AmendmentNotes',
    label: 'Amendment Notes',
    category: 'amendment',
    type: 'text',
    description: 'Notes about the amendment',
  },

  // System Information
  {
    name: 'LoadDate',
    label: 'Load Date',
    category: 'system',
    type: 'date',
    description: 'Date when data was loaded',
  },
  {
    name: 'ImportTimestamp',
    label: 'Import Timestamp',
    category: 'system',
    type: 'datetime',
    description: 'When data was imported',
  },
  {
    name: 'ImportSource',
    label: 'Import Source',
    category: 'system',
    type: 'text',
    description: 'Source of imported data',
  },
  {
    name: 'source_file',
    label: 'Source File',
    category: 'system',
    type: 'text',
    description: 'Source file name',
  },
  {
    name: 'IsAmended',
    label: 'Is Amended',
    category: 'system',
    type: 'boolean',
    description: 'Whether ticket has been amended',
  },
];

const CATEGORIES: readonly { value: string; label: string; icon: string }[] = [
  { value: 'all', label: 'All Columns', icon: '📋' },
  { value: 'basic', label: 'Basic Info', icon: '📄' },
  { value: 'location', label: 'Locations', icon: '📍' },
  { value: 'weight', label: 'Weights', icon: '⚖️' },
  { value: 'transport', label: 'Transport', icon: '🚛' },
  { value: 'product', label: 'Products', icon: '📦' },
  { value: 'security', label: 'Security', icon: '🔒' },
  { value: 'timestamp', label: 'Timestamps', icon: '⏰' },
  { value: 'tags', label: 'Tags', icon: '🏷️' },
  { value: 'amendment', label: 'Amendments', icon: '✏️' },
  { value: 'system', label: 'System', icon: '⚙️' },
];

export interface DynamicColumnSelectorProps {
  onAddFilter: (column: DynamicColumn) => void;
  /** Columns that already have a filter (not offered again). */
  activeFilters?: string[];
}

const DynamicColumnSelector = ({ onAddFilter, activeFilters = [] }: DynamicColumnSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const availableColumns = AVAILABLE_COLUMNS;
  const categories = CATEGORIES;
  // Saved filters on a column no longer offered do not count against the total
  const activeCount = availableColumns.filter((c) => activeFilters.includes(c.name)).length;

  // Filter columns based on search term and category
  const filteredColumns = availableColumns.filter((column) => {
    const matchesSearch =
      column.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      column.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      column.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || column.category === selectedCategory;
    const notAlreadyActive = !activeFilters.includes(column.name);

    return matchesSearch && matchesCategory && notAlreadyActive;
  });

  const handleAddFilter = (column: DynamicColumn) => {
    onAddFilter(column);
    setSearchTerm('');
    setIsOpen(false);
  };

  const getTypeIcon = (type: DynamicColumnType) => {
    switch (type) {
      case 'text':
        return '📝';
      case 'number':
        return '🔢';
      case 'date':
        return '📅';
      case 'datetime':
        return '🕐';
      case 'boolean':
        return '☑️';
      default:
        return '📄';
    }
  };

  return (
    <div className="relative">
      {/* Add Filter Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
      >
        <PlusIcon className="h-4 w-4 mr-1" />
        Add Column Filter
        {isOpen ? (
          <ChevronUpIcon className="h-4 w-4 ml-1" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 ml-1" />
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-96 bg-surface border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900 flex items-center">
                <TableCellsIcon className="h-4 w-4 mr-2 text-primary-600" />
                Add Column Filter
              </h4>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search columns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Category Filter */}
            <div className="mb-3">
              <ThemedSelect
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(String(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.icon} {category.label}
                  </option>
                ))}
              </ThemedSelect>
            </div>

            {/* Column List */}
            <div className="max-h-64 overflow-y-auto">
              {filteredColumns.length > 0 ? (
                <div className="space-y-1">
                  {filteredColumns.map((column) => (
                    <button
                      key={column.name}
                      onClick={() => handleAddFilter(column)}
                      className="w-full text-left p-2 rounded-md hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center">
                            <span className="text-sm mr-2">{getTypeIcon(column.type)}</span>
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {column.label}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1 truncate">
                            {column.description}
                          </p>
                        </div>
                        <PlusIcon className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <TableCellsIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No columns found</p>
                  <p className="text-xs">Try adjusting your search or category filter</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                {filteredColumns.length} of {availableColumns.length - activeCount} columns
                available
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicColumnSelector;
